import { useMemo, useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import type { Lens } from "@/profitability/interactive-report/period-lens";
import { ReportView } from "@/profitability/interactive-report/report-view";
import { RowDetail } from "@/profitability/interactive-report/row-detail";
import {
  type OpenRowDetail,
  RowDetailValueProvider,
} from "@/profitability/interactive-report/row-detail-context";
import {
  MarkReviewedButton,
  usePeriodReview,
} from "@/profitability/overview/period-review";
import {
  findRecord,
  type RecordObject,
  type RecordRef,
  recordId,
} from "@/profitability/records/objects";
import { ReviewScopeProvider } from "@/profitability/records/objects-context";
import type { RecordList } from "@/profitability/records/record-panel";
import { RecordSheet } from "@/profitability/records/record-sheet";
import type { FindingsReviewState } from "@/profitability/review/query";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type {
  ProfitabilityRun,
  ReportControlValues,
  ReportOutput,
} from "@/profitability/types";
import { runDates } from "@/profitability/utils";

/**
 * The panes the template's script declared, for the period on screen, each
 * as its own tab of the template page. They are the run page's dashboard,
 * drawn by the same view: this only decides where a row opens (a record in
 * the page's record sheet, any other row in a sheet of its own) and holds
 * the filters, so one set on a pane is still set on the next.
 *
 * The page's header names the period. When the report has nothing to
 * review there is no Review tab to sign the period off on, so a line above
 * the pane carries the sign-off.
 */
export const TemplatePanes = ({
  businessId,
  reportId,
  run,
  output,
  pane,
  objects,
  selected,
  review,
  findingsReview,
  onPaneChange,
  onOpenRecord,
  onOpenRow,
}: {
  businessId: string;
  reportId: string;
  run: ProfitabilityRun;
  output: ReportOutput;
  /** The declared pane on screen. */
  pane: string;
  objects: RecordObject[];
  selected: RecordRef | null;
  /** Whether the page has a Review tab, which signs the period off. */
  review: boolean;
  findingsReview: FindingsReviewState;
  onPaneChange: (pane: string) => void;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
  /** A row that is not a record opened: the page closes what covers it. */
  onOpenRow: () => void;
}) => {
  const spec = useReviewSpec();
  const signOff = usePeriodReview(businessId, review ? "" : run.id);
  const [values, setValues] = useState<ReportControlValues>({});
  const [lens, setLens] = useState<Lens>(null);
  const [row, setRow] = useState<OpenRowDetail | null>(null);
  const dates = runDates(run);
  const rowDetail = useMemo(() => {
    const object = selected
      ? objects.find((item) => item.dataset.id === selected.dataset)
      : undefined;
    const record =
      object && selected && findRecord(object.dataset, selected.recordId);
    return {
      open:
        row ??
        (object && record ? { row: record, dataset: object.dataset } : null),
      openRow: (value: OpenRowDetail) => {
        const target = objects.find(
          (item) => item.dataset.id === value.dataset.id,
        );
        if (target) {
          setRow(null);
          onOpenRecord(
            {
              dataset: target.dataset.id,
              recordId: recordId(value.dataset, value.row),
            },
            value.list,
          );
          return;
        }
        onOpenRow();
        setRow(value);
      },
      closeRow: () => setRow(null),
    };
  }, [objects, onOpenRecord, onOpenRow, row, selected]);
  return (
    <Stack gap={3} align="stretch" className="min-h-0 w-full flex-1">
      {!review && (
        <Stack direction="row" justify="end">
          <MarkReviewedButton review={signOff} label={spec.labels.reviewed} />
        </Stack>
      )}
      <ReviewScopeProvider
        scope={{ businessId, reportId, runId: run.id, ...findingsReview }}
      >
        <RowDetailValueProvider value={rowDetail}>
          <ReportView
            businessId={businessId}
            runId={run.id}
            output={output}
            dates={dates}
            pane={pane}
            onPaneChange={onPaneChange}
            values={values}
            onValuesChange={setValues}
            lens={lens}
            onLensChange={setLens}
          />
        </RowDetailValueProvider>
      </ReviewScopeProvider>
      <RecordSheet
        open={!!row}
        onClose={() => setRow(null)}
        label={row?.dataset.title ?? "Row"}
      >
        {row && <RowDetail open={row} onClose={() => setRow(null)} />}
      </RecordSheet>
    </Stack>
  );
};
